import { useForm } from '@refinedev/antd';
import type { HttpError } from '@refinedev/core';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Divider, Form, Input, Row, Tooltip, Typography } from 'antd';
import { InputMask } from 'components/form-items/InputMask';
import { CreateLayout } from 'components/layout/layout/CreateLayout';
import { PageHeader } from 'components/professor_area/PageHeader';
import { IUser } from 'interfaces/users';
import { fullNameValidatorRule } from 'utils/validators';

export const UserCreate = () => {
  const { formProps, saveButtonProps, onFinish } = useForm<IUser>({
    errorNotification: (error: HttpError | undefined) => ({
      type: 'error',
      message:   error?.message ?? 'Tente novamente mais tarde',
      description:  'Não foi possível cadastrar o aluno.'
    }) 
  });

  const onSubmit = async (
    values: Record<string, unknown> & {
      personal_info_attributes?: {
        document_info?: string;
        phone_number?: string;
      };
    }
  ) => {
    try {
      await onFinish({
        ...values,
        personal_info_attributes: {
          ...values.personal_info_attributes,
          document_type: 'cpf',
          document_info: values.personal_info_attributes?.document_info?.replace(
            /\D/g,
            ''
          ),
          phone_number: values.personal_info_attributes?.phone_number?.replace(
            /\D/g,
            ''
          )
        }
      });
    } catch {
      // tratado pelo errorNotification
    }
  };

  return (
    <CreateLayout saveButtonProps={saveButtonProps} title="Criar aluno">
      <PageHeader title="Criar aluno" />

      <Card>
        <Form {...formProps} layout="vertical" onFinish={onSubmit} autoComplete="off">
          <Typography.Title level={5}>Conta</Typography.Title>
          <Row gutter={[16, 0]} align="top">
            <Col xs={24} md={12}>
              <Form.Item
                label="E-mail"
                name="email"
                rules={[
                  { required: true, message: 'Campo obrigatório' },
                  { type: 'email', message: 'Insira um e-mail válido' }
                ]}
              >
                <Input autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Senha"
                name="password"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório'
                  },
                  {
                    min: 8,
                    message: 'A senha deve ter pelo menos 8 caracteres'
                  }
                ]}
              >
                <Input type="password" autoComplete="new-password" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Typography.Title level={5}>Informações pessoais</Typography.Title>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Nome Completo
                    <Tooltip title="Utilizado para emissão de certificados. Insira o nome conforme documento oficial.">
                      <InfoCircleOutlined className="ml-1 text-xs text-blue-500 align-middle" />
                    </Tooltip>
                  </span>
                }
                name={['personal_info_attributes', 'full_name']}
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório'
                  },
                  fullNameValidatorRule()
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Como deseja ser chamado(a)"
                name="name"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="CPF"
                name={['personal_info_attributes', 'document_info']}
                rules={[
                  {
                    pattern: /^(?:\d{3}\.){2}\d{3}-\d{2}$/,
                    message: 'Insira um CPF válido'
                  },
                  {
                    required: true,
                    message: 'Campo obrigatório'
                  }
                ]}
              >
                <InputMask mask="000.000.000-00" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Telefone"
                name={['personal_info_attributes', 'phone_number']}
                rules={[
                  {
                    pattern: /^\(\d{2}\) \d{5}-\d{4}$/,
                    message: 'Insira um numero de telefone válido'
                  }
                ]}
              >
                <InputMask mask="(00) 00000-0000" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </CreateLayout>
  );
};
